import { useState } from 'react'
import { AlertTriangle, KeyRound, Plus, RefreshCw, Webhook } from 'lucide-react'
import {
  DateValue, EmptyState, ErrorState, FilterBar, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import {
  useDeliveries, useEventTypes, useIntegrationMutations, useSignatureRecipe, useWebhooks,
} from '../api'
import type { WebhookSecretView } from '../types'

/**
 * Where this system tells somebody that something happened.
 *
 * <p>The screen shows the two things a subscriber cannot work out for themselves: exactly how
 * to verify the signature, and what actually happened to each delivery — including the ones
 * that failed five times and stopped.
 */
export function WebhooksPage() {
  useBreadcrumb()
  const subscriptions = useWebhooks()
  const events = useEventTypes()
  const recipe = useSignatureRecipe()
  const actions = useIntegrationMutations()

  const [filter, setFilter] = useState('')
  const deliveries = useDeliveries(filter || undefined)
  const [draft, setDraft] = useState<{
    id?: string; name: string; targetUrl: string; eventTypes: string[]
  } | null>(null)
  const [secret, setSecret] = useState<WebhookSecretView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [banner, setBanner] = useState<string | null>(null)

  const run = (work: () => Promise<unknown>, fallback: string) => {
    void (async () => {
      setBanner(null)
      try {
        await work()
      } catch (caught) {
        setBanner(toApiProblem(caught)?.detail ?? fallback)
      }
    })()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Webhooks"
        subtitle="What this system sends out, to whom, and what came back."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({ name: '', targetUrl: '', eventTypes: [] })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New subscription
          </Button>
        }
      />

      {banner && (
        <p role="alert" className="rounded-md border border-[var(--color-danger)] bg-[var(--color-tint-red)] p-3 text-sm text-[var(--color-danger)]">
          {banner}
        </p>
      )}

      <Card className="p-3">
        <p className="flex items-center gap-2 text-sm font-medium text-[var(--color-heading)]">
          <Webhook className="size-4" aria-hidden="true" />
          How a receiver checks a delivery is really from here
        </p>
        <p className="mt-1 text-sm text-[var(--color-body)]">{recipe.data ?? '…'}</p>
        <p className="mt-2 text-xs text-[var(--color-muted)]">
          An event is written in the same transaction as the change it describes, so a
          subscriber never hears about something that was rolled back. Failed deliveries are
          retried after 1 minute, 5, 30, 2 hours and 12 — then they stop and say so here.
        </p>
      </Card>

      <Card>
        {subscriptions.isPending && <Skeleton className="m-4 h-24" />}
        {subscriptions.isError && (
          <ErrorState error={subscriptions.error} onRetry={() => void subscriptions.refetch()} />
        )}
        {subscriptions.isSuccess && subscriptions.data.length === 0 && (
          <EmptyState
            title="Nobody is listening"
            description="Nothing is sent anywhere until a subscription exists. Events are kept for the API to read either way."
          />
        )}
        {subscriptions.isSuccess && subscriptions.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
                <tr>
                  <th className="p-3 text-start font-medium">Subscription</th>
                  <th className="p-3 text-start font-medium">Events</th>
                  <th className="p-3 text-start font-medium">Health</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {subscriptions.data.map((subscription) => (
                  <tr key={subscription.id} className="border-b border-[var(--color-border)] align-top">
                    <td className="p-3">
                      <p className="font-medium text-[var(--color-heading)]">{subscription.name}</p>
                      <p className="max-w-xs truncate font-mono text-xs text-[var(--color-muted)]"
                        title={subscription.targetUrl}>
                        {subscription.targetUrl}
                      </p>
                    </td>
                    <td className="p-3">
                      <div className="flex max-w-xs flex-wrap gap-1">
                        {subscription.eventTypes.map((type) => (
                          <span key={type} className="rounded bg-[var(--color-page)] px-1.5 py-0.5 font-mono text-xs">
                            {type}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={subscription.status}
                        tone={subscription.status === 'ACTIVE'
                          ? 'success'
                          : subscription.status === 'SUSPENDED' ? 'danger' : 'neutral'}
                        label={subscription.status === 'ACTIVE'
                          ? 'Active'
                          : subscription.status === 'SUSPENDED' ? 'Suspended' : 'Paused'}
                      />
                      {subscription.suspendedReason && (
                        <p className="mt-1 flex max-w-xs items-start gap-1 text-xs text-[var(--color-danger)]">
                          <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
                          {subscription.suspendedReason}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-[var(--color-muted)]">
                        {subscription.lastSuccessAt
                          ? <>Last delivered <DateValue value={subscription.lastSuccessAt} withTime /></>
                          : 'Nothing delivered yet'}
                        {subscription.deadDeliveries > 0
                          && ` · ${subscription.deadDeliveries} gave up`}
                      </p>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap justify-end gap-2">
                        <Button
                          type="button" variant="outline" size="sm"
                          onClick={() => {
                            setError(null)
                            setDraft({
                              id: subscription.id, name: subscription.name,
                              targetUrl: subscription.targetUrl,
                              eventTypes: [...subscription.eventTypes],
                            })
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button" variant="outline" size="sm"
                          onClick={() => run(
                            async () => setSecret(
                              await actions.rotateWebhookSecret.mutateAsync(subscription.id)),
                            'That secret could not be rotated')}
                        >
                          <KeyRound className="size-4" aria-hidden="true" />
                          Rotate
                        </Button>
                        <Button
                          type="button" variant="outline" size="sm"
                          onClick={() => run(
                            () => actions.setWebhookStatus.mutateAsync({
                              id: subscription.id,
                              status: subscription.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE',
                            }),
                            'That subscription could not be changed')}
                        >
                          {subscription.status === 'ACTIVE' ? 'Pause' : 'Resume'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FilterBar>
        <Select
          aria-label="Subscription"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        >
          <option value="">Every subscription</option>
          {subscriptions.data?.map((subscription) => (
            <option key={subscription.id} value={subscription.id}>{subscription.name}</option>
          ))}
        </Select>
      </FilterBar>

      <Card>
        <p className="border-b border-[var(--color-border)] p-3 text-sm font-medium text-[var(--color-heading)]">
          Deliveries
        </p>
        {deliveries.isPending && <Skeleton className="m-4 h-20" />}
        {deliveries.isError && (
          <ErrorState error={deliveries.error} onRetry={() => void deliveries.refetch()} />
        )}
        {deliveries.isSuccess && deliveries.data.length === 0 && (
          <EmptyState
            title="Nothing sent yet"
            description="Deliveries appear here within a minute of an event happening."
          />
        )}
        {deliveries.isSuccess && deliveries.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-[var(--color-border)] text-[var(--color-muted)]">
                <tr>
                  <th className="p-3 text-start font-medium">When</th>
                  <th className="p-3 text-start font-medium">Event</th>
                  <th className="p-3 text-start font-medium">To</th>
                  <th className="p-3 text-start font-medium">Result</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {deliveries.data.map((delivery) => (
                  <tr key={delivery.id} className="border-b border-[var(--color-border)] align-top">
                    <td className="p-3"><DateValue value={delivery.createdAt} withTime /></td>
                    <td className="p-3 font-mono text-xs">
                      {delivery.eventType}
                      {delivery.redeliveryOf && (
                        <span className="block text-[var(--color-muted)]">a redelivery</span>
                      )}
                    </td>
                    <td className="p-3">{delivery.subscriptionName ?? '—'}</td>
                    <td className="p-3">
                      <StatusBadge
                        status={delivery.status}
                        tone={delivery.status === 'DELIVERED'
                          ? 'success'
                          : delivery.status === 'DEAD' ? 'danger' : 'warning'}
                        label={delivery.status === 'DELIVERED'
                          ? `${delivery.responseCode} in ${delivery.durationMs ?? 0}ms`
                          : delivery.status === 'DEAD' ? 'Gave up' : `Attempt ${delivery.attempt}`}
                      />
                      {delivery.error && (
                        <p className="mt-1 max-w-sm text-xs text-[var(--color-muted)]">{delivery.error}</p>
                      )}
                      {delivery.nextAttemptAt && (
                        <p className="mt-1 text-xs text-[var(--color-muted)]">
                          Next try <DateValue value={delivery.nextAttemptAt} withTime />
                        </p>
                      )}
                    </td>
                    <td className="p-3 text-end">
                      <Button
                        type="button" variant="outline" size="sm"
                        onClick={() => run(
                          () => actions.redeliver.mutateAsync(delivery.id),
                          'That event could not be sent again')}
                      >
                        <RefreshCw className="size-4" aria-hidden="true" />
                        Redeliver
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setError(null) } }}
        title={draft?.id ? 'Edit subscription' : 'New subscription'}
        submitLabel="Save"
        loading={actions.subscribe.isPending || actions.updateWebhook.isPending}
        error={error}
        onSubmit={() => {
          if (!draft) return
          void (async () => {
            setError(null)
            try {
              if (draft.id) {
                await actions.updateWebhook.mutateAsync({
                  id: draft.id, name: draft.name, targetUrl: draft.targetUrl,
                  eventTypes: draft.eventTypes,
                })
                setDraft(null)
              } else {
                const created = await actions.subscribe.mutateAsync({
                  name: draft.name, targetUrl: draft.targetUrl, eventTypes: draft.eventTypes,
                })
                setDraft(null)
                setSecret(created)
              }
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That subscription could not be saved')
            }
          })()
        }}
      >
        {draft && (
          <div className="grid gap-3">
            <div>
              <Label htmlFor="hook-name">Name</Label>
              <Input
                id="hook-name" value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="hook-url">Where to post it</Label>
              <Input
                id="hook-url" value={draft.targetUrl} placeholder="https://example.com/hooks/hrms"
                onChange={(event) => setDraft({ ...draft, targetUrl: event.target.value })}
              />
            </div>
            <div>
              <Label>Events</Label>
              <p className="mb-2 text-xs text-[var(--color-muted)]">
                Only what this product actually emits. Anything missing is missing because
                nothing publishes it yet.
              </p>
              <div className="space-y-2">
                {events.data?.map((event) => (
                  <label key={event.type} className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-0.5 size-4"
                      aria-label={event.type}
                      checked={draft.eventTypes.includes(event.type)}
                      onChange={(changed) => setDraft({
                        ...draft,
                        eventTypes: changed.target.checked
                          ? [...draft.eventTypes, event.type]
                          : draft.eventTypes.filter((type) => type !== event.type),
                      })}
                    />
                    <span>
                      <span className="font-mono text-xs">{event.type}</span>
                      <span className="block text-xs text-[var(--color-muted)]">
                        {event.description}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={secret != null}
        onOpenChange={(open) => { if (!open) setSecret(null) }}
        title="The signing secret, once"
        submitLabel="Done"
        onSubmit={() => setSecret(null)}
      >
        {secret && (
          <div className="space-y-3 text-sm">
            <code className="block break-all rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-2 font-mono text-xs">
              {secret.signingSecret}
            </code>
            <p className="text-[var(--color-body)]">{secret.note}</p>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
